import { ShopifySectionRenderingSchema } from '@/scripts/types/theme'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	ATTRIBUTES,
	SELECTORS,
} from '@/scripts/core/global'
import {
	CartAddWithSections,
	CartUpdateWithSections,
	hasRequiredCartSections,
	renderRawHTMLToDOM,
	showCartErrorNotification,
} from '@/scripts/core/cart-functions'
import type { Cart } from '@/scripts/shopify'

export class CartDrawer extends UcoastEl {
	static htmlSelector = 'cart-drawer'
	static selectors = {
		overlay: '[data-uc-cart-drawer-overlay]',
		inner: '[data-uc-cart-drawer-inner]',
		container: '#CartDrawer',
		cartLink: '#CartIconBubble',
		closeButton: '[data-uc-cart-drawer-close-button]',
		noteSummary: '[data-uc-cart-note-summary]',
		noteDetails: '[data-uc-cart-note-details]',
		itemCount: '[data-uc-cart-drawer-count]',
	}
	activeElement?: HTMLElement
	private openTimer?: number
	constructor() {
		super()

		this.addEventListener(
			'keyup',
			(evt) => evt.code === 'Escape' && this.close()
		)
		this.getOverlay().addEventListener('click', this.close.bind(this))
		this.setHeaderCartIconAccessibility()
		this.maybeAutoOpen()
	}

	getOverlay() {
		return q.rs(CartDrawer.selectors.overlay, this)
	}

	maybeAutoOpen() {
		const url = new URL(window.location.href)
		const urlParams = url.searchParams
		if (urlParams.get('open') === 'cart') {
			this.open()
		}
	}

	setHeaderCartIconAccessibility() {
		const cartLink = q.rs(CartDrawer.selectors.cartLink)
		cartLink.setAttribute('role', 'button')
		cartLink.setAttribute('aria-haspopup', 'dialog')
		cartLink.addEventListener('click', (event) => {
			event.preventDefault()
			this.open(cartLink)
		})
		cartLink.addEventListener('keydown', (event) => {
			if (event.code?.toUpperCase() === 'SPACE') {
				event.preventDefault()
				this.open(cartLink)
			}
		})
	}

	open(triggeredBy?: HTMLElement) {
		if (triggeredBy) this.setActiveElement(triggeredBy)
		const cartDrawerNote = q.os(CartDrawer.selectors.noteSummary, this)
		if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
			this.setSummaryAccessibility(cartDrawerNote)
		window.clearTimeout(this.openTimer)
		this.openTimer = window.setTimeout(() => {
			if (!this.isConnected) return
			this.classList.add('animate', 'active')
			const inner = q.rs(CartDrawer.selectors.inner, this)
			window.TsDOM.trapFocus(inner, inner)
			void window.Ucoast.mediaManager.playAllInContainer(this)
		}, 1)

		document.body.classList.add('overflow-hidden')
	}

	close() {
		window.clearTimeout(this.openTimer)
		this.classList.remove('active')
		window.TsDOM.removeTrapFocus(this.activeElement)
		document.body.classList.remove('overflow-hidden')
	}

	setSummaryAccessibility(cartDrawerNote: HTMLElement) {
		const parentElement = cartDrawerNote.parentElement
		if (!parentElement)
			throw new Error(
				'setSummaryAccessibility failed - No parent element found'
			)
		cartDrawerNote.setAttribute('role', 'button')
		cartDrawerNote.setAttribute('aria-expanded', 'false')
		const nextElementSibling = cartDrawerNote.nextElementSibling

		if (
			nextElementSibling instanceof HTMLElement &&
			nextElementSibling.hasAttribute('id')
		) {
			cartDrawerNote.setAttribute('aria-controls', nextElementSibling.id)
		}

		cartDrawerNote.addEventListener('click', (event: MouseEvent) => {
			const currentTarget = q.rct(event)
			const isExpanded = q
				.rs(CartDrawer.selectors.noteDetails, this)
				.hasAttribute('open')
			currentTarget.setAttribute('aria-expanded', `${isExpanded}`)
		})

		parentElement.addEventListener('keyup', q.onKeyUpEscape)
	}

	showError(message = window.cartStrings.error) {
		if (showCartErrorNotification(message)) return
		const errors = q.os<HTMLElement>('[data-uc-cart-drawer-errors]', this)
		if (errors) errors.textContent = message
	}

	renderContents(
		cart: CartAddWithSections | CartUpdateWithSections,
		shouldOpen = true
	) {
		const requiredSections = this.getSectionsToRender()
			.map((section) => section.section)
			.filter((sectionId): sectionId is string => Boolean(sectionId))
		if (!hasRequiredCartSections(cart, requiredSections)) {
			this.showError()
			return
		}

		this.setActiveElement(document.activeElement)
		try {
			this.getSectionsToRender().forEach((section) => {
				const sectionHandle = section.section
				if (!sectionHandle) throw new Error('Section id is required')
				renderRawHTMLToDOM({
					sourceHTML: cart.sections[sectionHandle],
					sourceSelector: section.selector,
					destinationSelector: section.selector,
					destinationSelectorContainer: `#${section.id}`,
				})
			})
			this.updateItemCount(cart)
		} catch (error) {
			console.error('Error rendering cart drawer contents', error)
			this.showError()
			return
		}

		setTimeout(() => {
			this.getOverlay().addEventListener('click', this.close.bind(this))
			if (shouldOpen) this.open()
			if (cart.items.length) {
				this.removeAttribute(ATTRIBUTES.cartEmpty)
			} else {
				this.setAttribute(ATTRIBUTES.cartEmpty, '')
			}
			this.syncCheckoutAvailability(!cart.items.length)
			void window.Ucoast.mediaManager.playAllInContainer(this)
		}, 1)
	}

	syncCheckoutAvailability(isEmpty: boolean) {
		const checkout = q.os<HTMLButtonElement>('#CartDrawer-Checkout', this)
		if (checkout) checkout.disabled = isEmpty
	}

	updateItemCount(cart: CartAddWithSections | CartUpdateWithSections | Cart) {
		const count = q.os<HTMLElement>(CartDrawer.selectors.itemCount, this)
		if ('item_count' in cart) {
			this.syncCheckoutAvailability(cart.item_count === 0)
		} else if ('items' in cart) {
			this.syncCheckoutAvailability(cart.items.length === 0)
		}
		if (!count) return
		if ('item_count' in cart) {
			count.textContent = `${cart.item_count}`
			return
		}
		const bubbleCount = q.os<HTMLElement>(
			'.cart-count-bubble span[aria-hidden="true"]'
		)
		if (bubbleCount) count.textContent = bubbleCount.textContent?.trim() ?? ''
	}

	getSectionsToRender(): ShopifySectionRenderingSchema[] {
		return [
			{
				id: 'CartDrawer',
				section: 'cart-drawer-items',
				selector: 'cart-drawer-items',
			},
			{
				id: 'CartIconBubble',
				section: 'cart-icon-bubble',
				selector: SELECTORS.cartLink,
			},
			{
				id: 'CartDrawer',
				section: 'dynamic-cart-footer',
				selector: '.cart-drawer__footer',
			},
		]
	}

	setActiveElement(element: Element | null) {
		if (!element) throw new Error('Active element is required')
		this.activeElement = element as HTMLElement
	}
}
